import { useCallback, useEffect, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { getProfileAnalytics } from "@/api/api";
import type { ProfileAnalytics, ProfileAnalyticsWeek } from "@/api/models";
import ErrorDetails from "@/components/Message/ErrorDetails";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  notation: "compact",
});

const fullNumberFormatter = new Intl.NumberFormat("en-US");

const formatTokens = (tokens: number): string => numberFormatter.format(tokens);

const formatWeek = (week: string): string => {
  const parsed = new Date(`${week}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return week;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(parsed);
};

const hasAnalyticsData = (analytics: ProfileAnalytics): boolean =>
  analytics.lifetimeTokens > 0
  || analytics.longestChatTokens > 0
  || analytics.weekly.some((entry) => entry.totalTokens > 0);

const getPeakWeek = (analytics: ProfileAnalytics): ProfileAnalyticsWeek | null => {
  if (analytics.peakWeek) return analytics.peakWeek;
  return analytics.weekly.reduce<ProfileAnalyticsWeek | null>(
    (peak, entry) => (!peak || entry.totalTokens > peak.totalTokens ? entry : peak),
    null,
  );
};

const WEEKS_PER_YEAR = 52;

const formatMonth = (week: string): string => {
  const parsed = new Date(`${week}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return week;
  return new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(parsed);
};

/**
 * Weeks with no usage are simply absent from the API response, so plotting the
 * raw array would compress an inactive stretch into nothing. Walk back a year
 * from the most recent week instead, filling the gaps with zeroes, so the axis
 * is continuous and a quiet period reads as quiet rather than disappearing.
 */
const buildTrailingYear = (weeks: ProfileAnalyticsWeek[]): ProfileAnalyticsWeek[] => {
  if (!weeks.length) return [];
  const totalsByWeek = new Map(weeks.map((entry) => [entry.week, entry.totalTokens]));
  const latest = new Date(`${weeks[weeks.length - 1].week}T00:00:00Z`);
  if (Number.isNaN(latest.getTime())) return weeks.slice(-WEEKS_PER_YEAR);

  const series: ProfileAnalyticsWeek[] = [];
  for (let offset = WEEKS_PER_YEAR - 1; offset >= 0; offset -= 1) {
    const cursor = new Date(latest);
    cursor.setUTCDate(cursor.getUTCDate() - offset * 7);
    const key = cursor.toISOString().slice(0, 10);
    series.push({ week: key, totalTokens: totalsByWeek.get(key) ?? 0 });
  }
  return series;
};

type WeeklyTooltipProps = { active?: boolean; payload?: { payload: ProfileAnalyticsWeek }[] };

const WeeklyTokenTooltip = ({ active, payload }: WeeklyTooltipProps) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-elevated">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Week of {formatWeek(entry.week)}</p>
      <p className="mt-1 text-sm font-semibold tabular-nums text-ink">{fullNumberFormatter.format(entry.totalTokens)} tokens</p>
    </div>
  );
};

const WeeklyTokenChart = ({ weeks }: { weeks: ProfileAnalyticsWeek[] }) => {
  const series = useMemo(() => buildTrailingYear(weeks), [weeks]);
  const maxTokens = Math.max(...series.map((entry) => entry.totalTokens), 0);
  const chartLabel = series.length
    ? `Weekly token totals over the last ${series.length} weeks. Highest week: ${formatTokens(maxTokens)} tokens.`
    : "No weekly token activity yet.";
  // ~6 labels across a year of weeks -- one tick per week is unreadable.
  const tickInterval = Math.max(1, Math.ceil(series.length / 6) - 1);

  return (
    <div className="mt-4 rounded-xl border border-line bg-canvas px-3 pb-3 pt-4 sm:px-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-ink">Weekly activity</h3>
        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Last 12 months</span>
      </div>
      <div className="h-48 w-full" role="img" aria-label={chartLabel}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="weekly-tokens-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0076bf" stopOpacity={0.32} />
                <stop offset="100%" stopColor="#0076bf" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="currentColor" strokeDasharray="2 8" className="text-line" />
            <XAxis
              dataKey="week"
              tickFormatter={formatMonth}
              interval={tickInterval}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              stroke="currentColor"
              className="text-ink-3"
              tick={{ fontSize: 11, fill: "currentColor" }}
            />
            <YAxis
              width={44}
              tickFormatter={formatTokens}
              tickLine={false}
              axisLine={false}
              stroke="currentColor"
              className="text-ink-3"
              tick={{ fontSize: 11, fill: "currentColor" }}
            />
            <Tooltip content={<WeeklyTokenTooltip />} cursor={{ stroke: "#0076bf", strokeOpacity: 0.45, strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey="totalTokens"
              stroke="#0076bf"
              strokeWidth={2}
              fill="url(#weekly-tokens-fill)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, className: "stroke-canvas" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <ul className="sr-only">
        {series.map((entry) => (
          <li key={entry.week}>{`${formatWeek(entry.week)}: ${fullNumberFormatter.format(entry.totalTokens)} tokens`}</li>
        ))}
      </ul>
    </div>
  );
};

const ProfileAnalytics = () => {
  const [analytics, setAnalytics] = useState<ProfileAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setAnalytics(await getProfileAnalytics());
    } catch (loadError) {
      setError(loadError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  const peakWeek = useMemo(() => (analytics ? getPeakWeek(analytics) : null), [analytics]);

  if (isLoading) {
    return (
      <div className="flex min-h-56 items-center justify-center" role="status" aria-live="polite">
        <span className="loading loading-ring text-primary" aria-label="Loading profile analytics" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-error/30 bg-error/5 p-5 dark:border-red-400/30 dark:bg-red-400/5" role="alert">
        <p className="text-sm font-medium text-ink">Unable to load your activity</p>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">{getErrorSummary(error)}</p>
        <ErrorDetails details={getErrorDetails(error)} error={error} />
        <button type="button" className="mt-4 inline-flex h-9 items-center justify-center rounded-lg border border-line-strong bg-surface px-3.5 text-[13px] font-medium text-ink transition-colors hover:bg-hover focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-surface" onClick={() => void loadAnalytics()}>
          Try again
        </button>
      </div>
    );
  }

  if (!analytics || !hasAnalyticsData(analytics)) {
    return (
      <div className="rounded-xl border border-line bg-canvas px-5 py-12 text-center">
        <p className="text-sm font-medium text-ink">No activity to show yet</p>
        <p className="mx-auto mt-2 max-w-xs text-xs leading-relaxed text-ink-2">Your token totals will appear here after your first chat.</p>
      </div>
    );
  }

  return (
    <section aria-labelledby="profile-analytics-heading">
      <dl className="grid grid-cols-2 divide-x divide-y divide-line overflow-hidden rounded-xl border border-line bg-canvas sm:grid-cols-3 sm:divide-y-0">
        <div className="p-4 sm:p-5">
          <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Lifetime tokens</dt>
          <dd className="mt-1.5 text-[1.75rem] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink">{formatTokens(analytics.lifetimeTokens)}</dd>
          <p className="mt-1.5 text-[11px] tabular-nums text-ink-3">{fullNumberFormatter.format(analytics.lifetimeTokens)} total</p>
        </div>
        <div className="p-4 sm:p-5">
          <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Peak week</dt>
          <dd className="mt-1.5 text-[1.75rem] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink">{peakWeek ? formatTokens(peakWeek.totalTokens) : "—"}</dd>
          <p className="mt-1.5 text-[11px] tabular-nums text-ink-3">{peakWeek ? `${formatWeek(peakWeek.week)} tokens` : "No weekly peak yet"}</p>
        </div>
        <div className="col-span-2 p-4 sm:col-span-1 sm:p-5">
          <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">Longest chat</dt>
          <dd className="mt-1.5 text-[1.75rem] font-semibold leading-none tracking-[-0.02em] tabular-nums text-ink">{formatTokens(analytics.longestChatTokens)}</dd>
          <p className="mt-1.5 text-[11px] tabular-nums text-ink-3">{fullNumberFormatter.format(analytics.longestChatTokens)} tokens</p>
        </div>
      </dl>

      <h2 id="profile-analytics-heading" className="sr-only">Profile activity</h2>
      <WeeklyTokenChart weeks={analytics.weekly} />
    </section>
  );
};

export default ProfileAnalytics;
